import { useContext, useEffect, useReducer, useRef } from "react";
import { EnrichmentCacheContext } from "./enrichment-cache";
import { compoundEventKey } from "./providers/types";

/**
 * Public hook for resolving external-calendar event titles client-side.
 *
 * Usage:
 *   const titles = useEventTitles([
 *     { provider: 'google', calendarId, eventId },
 *     ...
 *   ]);
 *   const result = titles.get(compoundEventKey('google', calendarId, eventId));
 *   // result?.title is the resolved string, undefined while loading,
 *   // result?.error gives a distinguished failure code.
 *
 * Rendering:
 *   - until resolved, components should render a structural placeholder
 *     ("Busy 9:00–9:30" or similar). No spinners on tiles — visual churn
 *     beats the placeholder according to the privacy plan.
 *   - on `error: 'permission_denied'`, a one-time "re-link calendar" toast
 *     should fire (the toast is owned by the page, not the hook — so the
 *     hook stays composable).
 *
 * Behavior:
 *   - dedupes within the cache TTL (5 min default)
 *   - batches requests via the EnrichmentCacheProvider's microtask flusher
 *   - re-fetches on TTL expiry or explicit invalidate()
 *   - subscribes to cache change notifications and re-renders on resolve
 *
 * If no EnrichmentCacheProvider is mounted (legacy code path), this hook
 * returns an empty Map and does nothing — components render their
 * placeholder forever, which is at least consistent.
 *
 * @param {ReadonlyArray<import('./providers/types').EnrichmentRequest>} requests
 * @returns {Map<string, import('./providers/types').EnrichmentResult>}
 */
export function useEventTitles(requests) {
  const ctx = useContext(EnrichmentCacheContext);
  const [, forceRender] = useReducer((x) => x + 1, 0);

  // Stable signature for the requests array — avoids re-running effects when
  // the consumer passes a freshly-constructed-but-equivalent array each
  // render (very common in calendar tile lists).
  const sigRef = useRef("");
  const sig = serializeRequests(requests);
  if (sig !== sigRef.current) {
    sigRef.current = sig;
  }

  useEffect(() => {
    if (!ctx) return undefined;
    if (!Array.isArray(requests) || requests.length === 0) return undefined;
    ctx.request(requests);
    const off = ctx.subscribe(forceRender);
    return off;
    // We deliberately depend on the stable signature, not the array
    // reference, so equivalent arrays don't re-trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx, sigRef.current]);

  const out = new Map();
  if (!ctx || !Array.isArray(requests)) return out;
  for (const req of requests) {
    if (!req || !req.provider || !req.calendarId || !req.eventId) continue;
    const key = compoundEventKey(req.provider, req.calendarId, req.eventId);
    const value = ctx.get(req);
    if (value) out.set(key, value);
  }
  return out;
}

function serializeRequests(reqs) {
  if (!Array.isArray(reqs)) return "";
  // Stable, order-independent signature.
  const keys = [];
  for (const r of reqs) {
    if (!r || !r.provider || !r.calendarId || !r.eventId) continue;
    keys.push(compoundEventKey(r.provider, r.calendarId, r.eventId));
  }
  keys.sort();
  return keys.join(",");
}

/**
 * Convenience: resolve a single event's title. Returns
 * `{ title, fetchedAt, error? } | undefined`. Most callers (e.g.
 * HideEventDialog, single-event tile) want this rather than the Map.
 *
 * @param {import('./providers/types').EnrichmentRequest | null | undefined} req
 * @returns {import('./providers/types').EnrichmentResult | undefined}
 */
export function useEventTitle(req) {
  const reqs = req ? [req] : [];
  const map = useEventTitles(reqs);
  if (!req) return undefined;
  return map.get(compoundEventKey(req.provider, req.calendarId, req.eventId));
}
